import { Box, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setRuntimeContext, setSettings } from '../../../../support/slices/tileBuilderSlice';
import { RootState, useAppDispatch } from '../../../../support/store';
import { TileDefinition } from '../../../../types/dashboard';
import { initialTileState, Query } from '../../../../types/tileBuilderTypes';
import DataVisualizationComponent from '../../../Common/DataVisualizationComponent';
import LeaderTile, { CurrentTimeFilter } from '../../../Leader/LeaderTile';
import GridLayout from 'react-grid-layout';
import DashboardGridCard from '../../../Dashboard/DashboardGrid/DashboardGridCard';
import { useSelector } from 'react-redux';
import { getChartKind } from './tilePreviewHelpers';

interface TilePreviewFrameProps {
  chartId: string;
  previewData: any;
  queries: Query[];
  chartType: string;
  chartCategory: number;
}

const stableStringify = (value: any): string => {
  const sortKeys = (obj: any): any => {
    if (Array.isArray(obj)) return obj.map(sortKeys);
    if (obj && typeof obj === 'object') {
      return Object.keys(obj)
        .sort()
        .reduce((acc: any, key) => {
          acc[key] = sortKeys(obj[key]);
          return acc;
        }, {});
    }
    return obj;
  };

  try {
    return JSON.stringify(sortKeys(value));
  } catch {
    return '';
  }
};

function TilePreviewFrame({
                            chartId,
                            previewData,
                            queries,
                            chartType,
                            chartCategory,
                          }: TilePreviewFrameProps) {
  const dispatch = useAppDispatch();

  const {
    displayContext = 'preview',
    runtimeContext: previewRuntimeContext = {},
    leaderLayout,
    selectedSourceIdx,
  } = useSelector((state: RootState) => state.tileBuilder?.[chartId]?.preview) || {};

  const { tileName, settings, transformations } = useSelector(
    (state: RootState) => state.tileBuilder?.[chartId]?.tile || initialTileState
  );

  const canvasWidth =
    typeof settings.boundarySize?.width === 'number' ? settings.boundarySize.width : 520;
  const canvasHeight =
    typeof settings.boundarySize?.height === 'number' ? settings.boundarySize.height : 520;

  let frameWidth: number;
  let frameHeight: number;

  if (displayContext === 'leader' && leaderLayout) {
    frameWidth = Math.min(leaderLayout.containerWidth, 700);
    frameHeight = leaderLayout.containerHeight;
  } else {
    const previewScale = (settings as any).previewScale || 1;
    frameWidth = Math.round(canvasWidth * previewScale);
    frameHeight = Math.round(canvasHeight * previewScale);
  }

  const chartKind = useMemo(() => getChartKind(chartType), [chartType]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const tileDefinition: TileDefinition = useMemo(() => {
    return {
      id: Number(chartId.split('-')[1]),
      chart_type: chartType,
      category: chartCategory,
      name: tileName || 'preview',
      show_legend: settings.show_legend,
      settings,
      queries,
      transformations,
      layout: null as any,
    };
  }, [chartId, chartType, chartCategory, tileName, settings, queries, transformations]);

  const previewCurrentTimeFilter = useMemo<CurrentTimeFilter>(() => {
    return (
      (previewRuntimeContext?.current_time_filter as CurrentTimeFilter | undefined) ??
      (settings?.defaultDateRange as CurrentTimeFilter | undefined) ??
      'all'
    );
  }, [previewRuntimeContext?.current_time_filter, settings?.defaultDateRange]);

  const mergedPreviewRuntimeContext = useMemo(
    () => ({
      ...(previewRuntimeContext ?? {}),
      current_time_filter: previewCurrentTimeFilter,
    }),
    [previewRuntimeContext, previewCurrentTimeFilter]
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const handleResize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };

    handleResize();

    const resizeObserver = new window.ResizeObserver(handleResize);
    resizeObserver.observe(containerRef.current);
    window.addEventListener('resize', handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const selectedDashboardLayout = useMemo(() => {
    const rawX = Number(tileDefinition.layout?.x);
    const rawY = Number(tileDefinition.layout?.y);
    const rawW = Number(tileDefinition.layout?.w);
    const rawH = Number(tileDefinition.layout?.h);

    const w = Number.isFinite(rawW) && rawW > 0 ? Math.min(rawW, 8) : 4;
    const h = Number.isFinite(rawH) && rawH > 0 ? rawH : 4;
    const x = Number.isFinite(rawX) ? Math.max(0, Math.min(rawX, 8 - w)) : 0;
    const y = Number.isFinite(rawY) ? Math.max(0, rawY) : 0;

    return {
      i: String(tileDefinition.id),
      x,
      y,
      w,
      h,
      minH: 1,
      minW: 1,
      maxH: 50,
      maxW: 8,
    };
  }, [tileDefinition]);

  const showBoundaryRect =
    (settings as any).enableEditRect === true &&
    chartKind !== 'metric' &&
    chartKind !== 'datagrid';

  const overrides = useMemo(
    () => ({ ...tileDefinition.settings, outlineTileContent: showBoundaryRect }),
    [tileDefinition.settings, showBoundaryRect]
  );

  const noopSetTileErrors = useCallback(
    (_value: React.SetStateAction<Record<number, string | null>>) => {},
    []
  );

  const handleLeaderPreviewTimeFilterChange = useCallback(
    (nextValue: CurrentTimeFilter) => {
      dispatch(
        setSettings({
          chartId,
          settings: {
            defaultDateRange: nextValue,
          },
        })
      );

      dispatch(
        setRuntimeContext({
          chartId,
          context: {
            ...(previewRuntimeContext ?? {}),
            current_time_filter: nextValue,
          },
        })
      );
    },
    [dispatch, chartId, previewRuntimeContext]
  );

  const chartVisualSettingsSignature = useMemo(
    () =>
      stableStringify({
        settings,
        chartType,
        chartCategory,
        chartKind,
        displayContext,
        previewCurrentTimeFilter,
        selectedSourceIdx,
        leaderLayout,
        queriesLength: queries.length,
        transformationsLength: transformations.length,
      }),
    [
      settings,
      chartType,
      chartCategory,
      chartKind,
      displayContext,
      previewCurrentTimeFilter,
      selectedSourceIdx,
      leaderLayout,
      queries.length,
      transformations.length,
    ]
  );

  const previewKey = useMemo(() => {
    return [
      chartId,
      chartKind,
      displayContext,
      selectedSourceIdx,
      previewCurrentTimeFilter,
      chartVisualSettingsSignature,
    ].join('|');
  }, [
    chartId,
    chartKind,
    displayContext,
    selectedSourceIdx,
    previewCurrentTimeFilter,
    chartVisualSettingsSignature,
  ]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'sticky',
        top: 12,
        alignSelf: 'flex-start',
        maxHeight: 'calc(92vh - 180px)',
        overflow: 'visible',
      }}
    >
      {displayContext !== 'leader' && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
          <TextField
            label="Canvas Width"
            type="number"
            size="small"
            value={canvasWidth}
            inputProps={{ step: 10, min: 100, max: 2000 }}
            onChange={(e) =>
              dispatch(
                setSettings({
                  chartId,
                  settings: {
                    boundarySize: {
                      width: Number(e.target.value),
                      height: canvasHeight,
                    },
                  },
                })
              )
            }
            sx={{ width: 130 }}
          />
          <TextField
            label="Canvas Height"
            type="number"
            size="small"
            value={canvasHeight}
            inputProps={{ step: 10, min: 100, max: 2000 }}
            onChange={(e) =>
              dispatch(
                setSettings({
                  chartId,
                  settings: {
                    boundarySize: {
                      width: canvasWidth,
                      height: Number(e.target.value),
                    },
                  },
                })
              )
            }
            sx={{ width: 130 }}
          />
          <TextField
            label="Preview Scale"
            type="number"
            size="small"
            value={(settings as any).previewScale || 1}
            inputProps={{ step: 0.1, min: 0.5, max: 2 }}
            onChange={(e) =>
              dispatch(
                setSettings({
                  chartId,
                  settings: { previewScale: Number(e.target.value) } as any,
                })
              )
            }
            sx={{ width: 140 }}
          />
        </Box>
      )}

      {displayContext === 'leader' && leaderLayout && (
        <Box sx={{ mb: 1, p: 1, bgcolor: 'info.light', borderRadius: 1, maxWidth: frameWidth }}>
          <Typography
            variant="caption"
            color="info.contrastText"
            sx={{ display: 'block', fontWeight: 600 }}
          >
            Leader: {leaderLayout.stripCount} tile{leaderLayout.stripCount !== 1 ? 's' : ''} ×{' '}
            {leaderLayout.containerHeight}px
          </Typography>
          <Typography variant="caption" color="info.contrastText" sx={{ display: 'block' }}>
            Chart adapts to available space (~{leaderLayout.chartAreaWidth}×
            {leaderLayout.chartAreaHeight}px)
          </Typography>
        </Box>
      )}

      {displayContext === 'leader' && leaderLayout ? (
        <Box
          sx={{
            border: '2px solid #1976d2',
            borderRadius: 1,
            bgcolor: '#fafafa',
            width: frameWidth,
            height: frameHeight,
            overflow: 'visible',
            position: 'relative',
            zIndex: 0,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              padding: '12px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              position: 'relative',
              zIndex: 0,
              boxSizing: 'border-box',
            }}
          >
            <div
              className="grid gap-3"
              style={{
                gridTemplateColumns: `repeat(${leaderLayout.stripCount}, minmax(0, 1fr))`,
                height: '100%',
                minHeight: 0,
                overflow: 'visible',
                position: 'relative',
                zIndex: 0,
              }}
            >
              {Array.from({ length: leaderLayout.stripCount }).map((_, index) => {
                if (index === 0) {
                  return (
                    <div
                      key={index}
                      style={{
                        minHeight: 0,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'visible',
                        position: 'relative',
                        zIndex: 0,
                      }}
                    >
                      <LeaderTile
                        key={previewKey}
                        tile={tileDefinition}
                        runtimeContext={mergedPreviewRuntimeContext}
                        setTileErrors={noopSetTileErrors}
                        err={null}
                        suppressReduxInit={false}
                        customData={previewData}
                        settingsOverride={{
                          ...settings,
                        }}
                        queriesOverride={queries}
                        transformationsOverride={transformations}
                        currentTimeFilterOverride={previewCurrentTimeFilter}
                        onCurrentTimeFilterOverrideChange={handleLeaderPreviewTimeFilterChange}
                        refetchTrigger={`leader-preview:${chartId}:${previewCurrentTimeFilter}:${chartVisualSettingsSignature}`}
                      />
                    </div>
                  );
                }

                return (
                  <div
                    key={index}
                    className="hbr-css__container-card p-2"
                    style={{
                      minHeight: 0,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'visible',
                      border: '1px solid #e0e0e0',
                      backgroundColor: '#f8f8f8',
                      position: 'relative',
                      zIndex: 0,
                      boxSizing: 'border-box',
                    }}
                  >
                    <div
                      className="hbr-css__text-primary-sm-semibold mb-1"
                      style={{
                        flex: '0 0 auto',
                        minHeight: 20,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {`Chart ${index + 1}`}
                    </div>

                    <div
                      style={{
                        flex: '1 1 auto',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'visible',
                        position: 'relative',
                        minHeight: 0,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          color: '#666',
                          fontSize: '0.75rem',
                          border: '1px dashed #ccc',
                        }}
                      >
                        Chart {index + 1}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Box>
        </Box>
      ) : displayContext === 'dashboard' ? (
        <Box
          sx={{
            position: 'relative',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            overflow: 'visible',
            width: frameWidth,
            height: frameHeight,
            maxWidth: frameWidth,
            boxSizing: 'border-box',
          }}
          className="test-box-wrapper"
        >
          <GridLayout
            className="layout min-w-[800px]"
            cols={8}
            rowHeight={50}
            width={containerSize.width || frameWidth}
            isBounded
            compactType="vertical"
            containerPadding={[0, 0]}
            margin={[8, 8] as any}
          >
            <div
              key={tileDefinition.id}
              data-grid={selectedDashboardLayout}
              style={{
                minHeight: 0,
                minWidth: 0,
                overflow: 'visible',
              }}
            >
              <DashboardGridCard
                tile={tileDefinition}
                width={frameWidth}
                settingsOverride={overrides}
              />
            </div>
          </GridLayout>
        </Box>
      ) : (
        <Box
          sx={{
            position: 'relative',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            width: frameWidth,
            height: frameHeight,
            overflow: 'visible',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: canvasWidth,
              height: canvasHeight,
              transformOrigin: 'top left',
              transform: `scale(${(settings as any).previewScale || 1})`,
              overflow: 'visible',
              position: 'absolute',
              top: 0,
              left: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <DataVisualizationComponent
              key={previewKey}
              tileDefinition={tileDefinition}
              customData={previewData}
              settingsOverride={overrides}
              queriesOverride={queries}
              transformationsOverride={transformations}
              runtimeContext={mergedPreviewRuntimeContext}
              currentTimeFilter={previewCurrentTimeFilter}
              refetchTrigger={`preview:${chartId}:${previewCurrentTimeFilter}:${chartVisualSettingsSignature}`}
              displayContext={displayContext}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default TilePreviewFrame;